import { fetchOrganizationMarketplacesThunk } from '@spike/marketplace-action';
import clsx from 'clsx';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner';
import { useSettingsApiClientWrapper, useSettingsMarketplace } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import LocationsTable from './LocationsTable';
import styles from './Locations.module.scss';

export interface LocationsProps {
    isMobile?: boolean;
    className?: string;
    openSidebar?: () => void;
}

export const Locations: FunctionComponent<LocationsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();
    const marketplace = useSettingsMarketplace();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const [isLoading, setIsLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        let active = true;
        setIsLoading(true);

        const request: unknown = dispatch(fetchOrganizationMarketplacesThunk(apiClientWrapper));

        if (request instanceof Promise) {
            request.finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        } else if (active) {
            setIsLoading(false);
        }

        return () => {
            active = false;
        };
        // Reload once each time this section is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredLocations = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return organizationMarketplaces;
        }

        return organizationMarketplaces.filter(location => location.name.toLowerCase().includes(query));
    }, [organizationMarketplaces, searchText]);

    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(filteredLocations.length / pageSize)) : 1;
    const safePage = Math.min(page, totalPages);

    const pageLocations = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return filteredLocations.slice(start, start + pageSize);
    }, [filteredLocations, pageSize, safePage]);

    const searchChangeHandler = (text: string) => {
        setSearchText(text);
        setPage(1);
    };

    const pageSizeChangeHandler = (size: number) => {
        setPageSize(size);
        setPage(1);
    };

    return (
        <div className={clsx(styles.container, props.className)}>
            <DefaultHeader
                title="Locations"
                textBelowTitle="View all your locations."
                onBack={props.isMobile ? props.openSidebar : undefined}
            />
            {isLoading ? (
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            ) : (
                <>
                    <SearchBar
                        placeholder="Search by location name"
                        searchText={searchText}
                        onChangeSearchText={searchChangeHandler}
                        className={styles.searchBar}
                    />
                    <LocationsTable
                        locations={pageLocations}
                        page={safePage}
                        pageSize={pageSize}
                        totalCount={filteredLocations.length}
                        fallbackCountryId={marketplace.basics.address.country?.id}
                        className={styles.table}
                        onPageChange={setPage}
                        onPageSizeChange={pageSizeChangeHandler}
                    />
                </>
            )}
        </div>
    );
};

export default Locations;
